import React, { useState, useEffect } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { HiTrendingUp, HiTrendingDown, HiClock, HiCheckCircle, HiUsers, HiClipboardList } from 'react-icons/hi'
import ReactECharts from 'echarts-for-react'

const Dashboard = () => {
  const { user } = useAuth()
  const [loading, setLoading] = useState(true)
  const [stats, setStats] = useState({
    totalIssues: 0,
    resolvedIssues: 0,
    pendingIssues: 0,
    activeUsers: 0,
    avgResponseTime: 0,
    satisfactionRate: 0
  })

  useEffect(() => {
    // Simulate loading data from API
    setTimeout(() => {
      setStats({
        totalIssues: 1247,
        resolvedIssues: 892,
        pendingIssues: 355,
        activeUsers: 2341,
        avgResponseTime: 2.3,
        satisfactionRate: 94.2
      })
      setLoading(false)
    }, 1000)
  }, [])

  const issueStatusChart = {
    title: {
      text: 'Issue Status Distribution',
      left: 'center',
      textStyle: {
        color: '#374151'
      }
    },
    tooltip: {
      trigger: 'item',
      formatter: '{a} <br/>{b}: {c} ({d}%)'
    },
    legend: {
      orient: 'vertical',
      left: 'left',
      textStyle: {
        color: '#374151'
      }
    },
    series: [
      {
        name: 'Issue Status',
        type: 'pie',
        radius: '50%',
        data: [
          { value: 892, name: 'Resolved', itemStyle: { color: '#10B981' } },
          { value: 234, name: 'In Progress', itemStyle: { color: '#F59E0B' } },
          { value: 121, name: 'Pending', itemStyle: { color: '#EF4444' } }
        ],
        emphasis: {
          itemStyle: {
            shadowBlur: 10,
            shadowOffsetX: 0,
            shadowColor: 'rgba(0, 0, 0, 0.5)'
          }
        }
      }
    ]
  }

  const monthlyTrendsChart = {
    title: {
      text: 'Monthly Issue Trends',
      left: 'center',
      textStyle: {
        color: '#374151'
      }
    },
    tooltip: {
      trigger: 'axis'
    },
    legend: {
      data: ['New Issues', 'Resolved Issues'],
      textStyle: {
        color: '#374151'
      }
    },
    xAxis: {
      type: 'category',
      data: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'],
      axisLabel: {
        color: '#374151'
      }
    },
    yAxis: {
      type: 'value',
      axisLabel: {
        color: '#374151'
      }
    },
    series: [
      {
        name: 'New Issues',
        type: 'line',
        data: [120, 132, 101, 134, 90, 230],
        itemStyle: { color: '#EF4444' }
      },
      {
        name: 'Resolved Issues',
        type: 'line',
        data: [110, 125, 95, 128, 85, 220],
        itemStyle: { color: '#10B981' }
      }
    ]
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Welcome Header */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
          Welcome back, {user?.full_name || user?.email || 'User'}!
        </h1>
        <p className="text-gray-600 dark:text-gray-300">
          Here's what's happening with your city's infrastructure today.
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-2 bg-blue-100 dark:bg-blue-900 rounded-lg">
              <HiClipboardList className="h-6 w-6 text-blue-600 dark:text-blue-400" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Issues</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.totalIssues.toLocaleString()}</p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-2 bg-green-100 dark:bg-green-900 rounded-lg">
              <HiCheckCircle className="h-6 w-6 text-green-600 dark:text-green-400" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Resolved</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.resolvedIssues.toLocaleString()}</p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-2 bg-yellow-100 dark:bg-yellow-900 rounded-lg">
              <HiClock className="h-6 w-6 text-yellow-600 dark:text-yellow-400" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Pending</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.pendingIssues.toLocaleString()}</p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-2 bg-purple-100 dark:bg-purple-900 rounded-lg">
              <HiUsers className="h-6 w-6 text-purple-600 dark:text-purple-400" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Active Users</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.activeUsers.toLocaleString()}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Performance Metrics */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Response Time</h3>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-3xl font-bold text-gray-900 dark:text-white">{stats.avgResponseTime} days</p>
              <p className="text-sm text-gray-600 dark:text-gray-400">Average response time</p>
            </div>
            <div className="flex items-center text-green-600 dark:text-green-400">
              <HiTrendingDown className="h-5 w-5 mr-1" />
              <span className="text-sm">12%</span>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Satisfaction Rate</h3>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-3xl font-bold text-gray-900 dark:text-white">{stats.satisfactionRate}%</p>
              <p className="text-sm text-gray-600 dark:text-gray-400">User satisfaction</p>
            </div>
            <div className="flex items-center text-green-600 dark:text-green-400">
              <HiTrendingUp className="h-5 w-5 mr-1" />
              <span className="text-sm">3%</span>
            </div>
          </div>
        </div>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <ReactECharts option={issueStatusChart} style={{ height: '400px' }} />
        </div>
        
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <ReactECharts option={monthlyTrendsChart} style={{ height: '400px' }} />
        </div>
      </div>

      {/* Recent Activity */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Recent Activity</h3>
        </div>
        <div className="p-6">
          <div className="space-y-4">
            <div className="flex items-center space-x-3">
              <div className="w-2 h-2 bg-green-500 rounded-full"></div>
              <span className="text-sm text-gray-600 dark:text-gray-400">
                Pothole on Main St. marked as resolved
              </span>
              <span className="text-xs text-gray-500">2 hours ago</span>
            </div>
            <div className="flex items-center space-x-3">
              <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
              <span className="text-sm text-gray-600 dark:text-gray-400">
                New streetlight issue reported on Oak Ave
              </span>
              <span className="text-xs text-gray-500">4 hours ago</span>
            </div>
            <div className="flex items-center space-x-3">
              <div className="w-2 h-2 bg-yellow-500 rounded-full"></div>
              <span className="text-sm text-gray-600 dark:text-gray-400">
                Sidewalk repair assigned to maintenance team
              </span>
              <span className="text-xs text-gray-500">6 hours ago</span>
            </div>
            <div className="flex items-center space-x-3">
              <div className="w-2 h-2 bg-green-500 rounded-full"></div>
              <span className="text-sm text-gray-600 dark:text-gray-400">
                Graffiti removal completed on Elm St.
              </span>
              <span className="text-xs text-gray-500">1 day ago</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Dashboard
